import { message as antdMessage } from 'antd';

/**
 * Utility to wrap Ant Design message methods with automatic scroll-to-top functionality
 * This ensures users don't miss important error messages that appear at the top of the screen
 */

/**
 * Smart scroll function that handles both regular pages and iframes (especially Bitrix24)
 */
const scrollToTop = () => {
	const performBasicScroll = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });

		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 50);

		// Try document.documentElement scroll
		if (document.documentElement) {
			document.documentElement.scrollTop = 0;
			document.documentElement.scrollTo?.(0, 0);
		}

		if (document.body) {
			document.body.scrollTop = 0;
			document.body.scrollTo?.(0, 0);
		}
	};

	const handleBitrix24 = () => {
		if (!window.BX24) {
			return;
		}

		if (window.BX24.scrollParentWindow) {
			window.BX24.scrollParentWindow(0);
		} else {
			if (window.BX24.fitWindow) {
				window.BX24.fitWindow();
			}

			setTimeout(() => {
				if (window.BX24.resizeWindow) {
					window.BX24.resizeWindow();
				}
			}, 100);
		}
	};

	const handleParentWindow = () => {
		if (!window.parent || window.parent === window) {
			return;
		}

		window.parent.scrollTo({ top: 0, behavior: 'smooth' });
		setTimeout(() => {
			window.parent.scrollTo(0, 0);
		}, 50);
	};

	const scrollContainers = () => {
		const scrollableElements = document.querySelectorAll('[style*="overflow"]');
		const commonScrollers = document.querySelectorAll(
			'.ant-layout-content, .scrollable, [data-scrollable], .main-content, #root',
		);
		const allElements = [...scrollableElements, ...commonScrollers];

		allElements.forEach((el) => {
			const hasScroll = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;

			if (hasScroll || el.scrollTop > 0) {
				el.scrollTop = 0;
				el.scrollTo?.(0, 0);
			}
		});
	};

	if (window.BX24 && window.parent !== window) {
		handleBitrix24();

		setTimeout(() => {
			performBasicScroll();
			handleParentWindow();
			scrollContainers();
		}, 200);
	} else {
		performBasicScroll();
		handleBitrix24();
		handleParentWindow();
		scrollContainers();
	}
};

const messageWithScroll = {
	/**
	 * Display an error message and automatically scroll to the top of the page
	 * @param {string|React.ReactNode} content - The error message content
	 * @param {number} duration - Duration in seconds (default: 3)
	 * @param {Function} onClose - Callback when message is closed
	 */
	error: (content, duration, onClose) => {
		// Call the original Ant Design message.error first
		const result = antdMessage.error(content, duration || 5, onClose);

		// Scroll to top after a small delay to ensure the message is rendered
		setTimeout(() => {
			scrollToTop();
		}, 100);

		return result;
	},

	/**
	 * Display a success message (keeping original behavior without scroll)
	 * @param {string|React.ReactNode} content - The success message content
	 * @param {number} duration - Duration in seconds (default: 3)
	 * @param {Function} onClose - Callback when message is closed
	 */
	success: (content, duration, onClose) => {
		return antdMessage.success(content, duration, onClose);
	},

	/**
	 * Display a warning message and automatically scroll to the top of the page
	 * @param {string|React.ReactNode} content - The warning message content
	 * @param {number} duration - Duration in seconds (default: 3)
	 * @param {Function} onClose - Callback when message is closed
	 */
	warning: (content, duration, onClose) => {
		// Call the original Ant Design message.warning first
		const result = antdMessage.warning(content, duration, onClose);

		// Scroll to top after a small delay to ensure the message is rendered
		setTimeout(() => {
			scrollToTop();
		}, 100);

		return result;
	},

	/**
	 * Display an info message (keeping original behavior without scroll)
	 * @param {string|React.ReactNode} content - The info message content
	 * @param {number} duration - Duration in seconds (default: 3)
	 * @param {Function} onClose - Callback when message is closed
	 */
	info: (content, duration, onClose) => {
		return antdMessage.info(content, duration, onClose);
	},

	/**
	 * Display a loading message (keeping original behavior without scroll)
	 * @param {string|React.ReactNode} content - The loading message content
	 * @param {number} duration - Duration in seconds (0 means it won't disappear automatically)
	 * @param {Function} onClose - Callback when message is closed
	 */
	loading: (content, duration, onClose) => {
		return antdMessage.loading(content, duration, onClose);
	},

	/**
	 * Global configuration for message component
	 */
	config: antdMessage.config,

	/**
	 * Destroy all messages
	 */
	destroy: antdMessage.destroy,
};

export { messageWithScroll as message };
export default messageWithScroll;
